import { Clock, Plus, X, AlertTriangle } from 'lucide-react';
import { formatDuration, type ProgramStart, type ScheduleStep } from '../../services/espnowIrrigation';
import { localHHMMToUtcSec, utcSecToLocalHHMM } from '../../utils/timezone';
import {
  cycleSec,
  findStartOverlaps,
  DAY_SEC,
  MAX_DURATION_PCT,
  MAX_STARTS,
  MIN_DURATION_PCT,
  MIN_START_GAP_SEC,
} from '../../utils/programStarts';

interface ProgramStartsEditorProps {
  starts: ProgramStart[];
  steps: ScheduleStep[] | null;
  orgTimezone: string;
  onChange: (starts: ProgramStart[]) => void;
}

/**
 * The times a program runs each day, and how long each run is.
 *
 * Most farms water the same way more than once a day, so rather than a second
 * program that has to be kept in step with the first, a program carries several
 * starts. Each has its own run length as a percentage of the valve times on the
 * steps below: an afternoon top-up at 50% runs every valve for half as long, in
 * the same order.
 *
 * Rows stay in the order they are edited in. Re-sorting while somebody types a
 * time would move the row out from under them; the list is put in time order
 * when the program is loaded or saved.
 */
export default function ProgramStartsEditor({ starts, steps, orgTimezone, onChange }: ProgramStartsEditorProps) {
  const overlaps = findStartOverlaps(starts, steps);
  const fullRunSec = cycleSec(steps, 100);

  function update(index: number, patch: Partial<ProgramStart>) {
    onChange(starts.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  function remove(index: number) {
    onChange(starts.filter((_, i) => i !== index));
  }

  /**
   * A new start lands four hours after the latest one, or later if a run takes
   * longer than that - somewhere it cannot collide with the run before it.
   */
  function add() {
    const latest = Math.max(...starts.map(s => s.start_time_sec));
    const needs = Math.ceil((fullRunSec + MIN_START_GAP_SEC) / 1800) * 1800;
    const at = (latest + Math.max(4 * 3600, needs)) % DAY_SEC;
    const taken = new Set(starts.map(s => s.start_time_sec));
    let t = at;
    while (taken.has(t)) t = (t + 1800) % DAY_SEC;
    onChange([...starts, { id: null, start_time_sec: t, duration_pct: 100 }]);
  }

  const label = (sec: number) => utcSecToLocalHHMM(sec, orgTimezone);

  return (
    <div>
      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide flex items-center gap-1">
        <Clock className="w-3.5 h-3.5" /> {starts.length > 1 ? 'Start times' : 'Start time'}
      </label>

      <div className="mt-1 space-y-2">
        {starts.map((start, i) => {
          const runSec = cycleSec(steps, start.duration_pct);
          return (
            <div key={start.id ?? `new-${i}`} className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <input
                type="time"
                aria-label={`Start time ${i + 1}`}
                value={label(start.start_time_sec)}
                onChange={e => {
                  const v = localHHMMToUtcSec(e.target.value, orgTimezone);
                  if (!isNaN(v)) update(i, { start_time_sec: v });
                }}
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={MIN_DURATION_PCT}
                  max={MAX_DURATION_PCT}
                  step={5}
                  aria-label={`Run length for start ${i + 1}, percent`}
                  value={start.duration_pct}
                  onChange={e => {
                    const n = parseInt(e.target.value, 10);
                    if (!isNaN(n)) update(i, { duration_pct: n });
                  }}
                  className="w-16 text-sm border border-gray-300 rounded-lg px-2 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 text-center"
                />
                <span className="text-xs text-gray-500">%</span>
              </div>
              <span className="text-xs text-gray-500">
                {runSec > 0
                  ? start.duration_pct === 100
                    ? `full run, ${formatDuration(runSec)}`
                    : `${formatDuration(runSec)} of ${formatDuration(fullRunSec)}`
                  : start.duration_pct === 100 ? 'full run' : `${start.duration_pct}% of each valve`}
              </span>
              {starts.length > 1 && (
                <button
                  type="button"
                  onClick={() => remove(i)}
                  aria-label={`Remove start ${label(start.start_time_sec)}`}
                  className="p-1 text-gray-500 hover:text-red-600 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          );
        })}
      </div>

      {starts.some(s => s.duration_pct < MIN_DURATION_PCT || s.duration_pct > MAX_DURATION_PCT) && (
        <p className="mt-1.5 text-xs text-red-700">
          Run length must be between {MIN_DURATION_PCT}% and {MAX_DURATION_PCT}%.
        </p>
      )}

      {overlaps.map(o => (
        <div
          key={`${o.start.start_time_sec}`}
          className="mt-2 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
        >
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5 text-red-500" />
          <p className="min-w-0">
            The {label(o.start.start_time_sec)} run takes {formatDuration(o.cycleSec)}, but the next
            start is at {label(o.next.start_time_sec)}. Leave at least {MIN_START_GAP_SEC / 60} minutes
            between the end of one run and the next start, or it will not save.
          </p>
        </div>
      ))}

      {starts.length < MAX_STARTS && (
        <button
          type="button"
          onClick={add}
          className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800"
        >
          <Plus className="w-3.5 h-3.5" /> Add start time
        </button>
      )}
      {starts.length > 1 && (
        <p className="mt-1 text-xs text-gray-500">
          Each start runs the same steps in the same order. Run length scales every valve&apos;s
          time — 50% runs each valve for half as long.
        </p>
      )}
    </div>
  );
}
